import { useEffect, useState } from 'react';

import type { FeatureFlagKey, FeatureFlagValue } from './types';
import { useLaunchDarkly } from './useLaunchDarkly';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Returns the value of a feature flag using the following sources (in that order):
 *
 * 1. Overrides (from `./feature-flags/overrides`)
 * 2. Actual values (from Launch Darkly)
 * 3. Local defaults (from `./feature-flags/defaults`)
 *
 * If none of these are available, `undefined` will be returned.
 *
 * The hook also supports optional live tracking, which ensures that the value is always up-to-date.
 */
export const useFeatureFlag = <Key extends FeatureFlagKey>(
  flagKey: Key,
  options?: UseFeatureFlagOptions,
) => {
  const {
    defaults,
    overrides,
    initialFeatureFlags,
    registerFeatureFlagListener,
    unregisterFeatureFlagListener,
  } = useLaunchDarkly();

  const shouldListenForChanges = Boolean(options?.listenForChanges);

  // ─── State ───────────────────────────────────────────────────────────

  const flagInitialValue = initialFeatureFlags?.[flagKey] ?? defaults[flagKey];

  const [featureFlagValue, setFeatureFlagValue] =
    useState<FeatureFlagValue<Key>>(flagInitialValue);

  // ─── Effects ─────────────────────────────────────────────────────────

  /**
   * Updates the feature flag's state, after getting a different initial value
   *
   * But when can the initial value be updated?
   *
   * This occurs after changing the identified customer in Launch Darkly and retrieving
   * new flags for it. For some reason, the LD native client does not trigger the change
   * event in those circumstances, hence we must monitor the initial feature flag
   * value change.
   *
   * @see {@link https://github.com/launchdarkly/react-native-client-sdk/issues/96 Related GitHub issue}
   *
   * When this issue is resolved, we will be able to delete this `useEffect` entirely
   * because LD will appropriately trigger the change event whenever the identified
   * customer changes (sign-in, sign-out).
   */
  useEffect(() => {
    setFeatureFlagValue(flagInitialValue);
  }, [flagInitialValue]);

  /**
   * Updates the feature flag value on change if the corresponding flag is enabled for it
   */
  useEffect(() => {
    if (!shouldListenForChanges) return;

    void registerFeatureFlagListener?.(flagKey, setFeatureFlagValue);

    return () => {
      void unregisterFeatureFlagListener?.(flagKey, setFeatureFlagValue);
    };
  }, [
    shouldListenForChanges,
    flagKey,
    registerFeatureFlagListener,
    unregisterFeatureFlagListener,
  ]);

  // ─────────────────────────────────────────────────────────────────

  const localOverride = overrides[flagKey];
  const localDefault = defaults[flagKey];

  return localOverride ?? featureFlagValue ?? localDefault;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseFeatureFlagOptions = {
  readonly listenForChanges: boolean;
};
